import { toUserTimezone } from 'utils/timezone/toUserTimezone';
import {
  FORM_ALREADY_SIGNED_STATUS,
  REQUEST_FORM_GENERIC_ERROR,
  SIGNING_PROVIDER_UNAVAILABLE_STATUS,
  SIGNING_UNAVAILABLE_MESSAGE,
  TEMPLATE_FIELD_MISMATCH_STATUS,
} from 'utils/signing/constants';

type ErrorResponse = {
  status?: number;
  data?:
    | { message?: string; form_type?: string; received_at?: string }
    | string;
};

const FORM_TYPE_LABELS: Record<string, string> = {
  intake: 'Intake Form',
  abn: 'ABN Form',
  lien: 'Lien Form',
};

/**
 * Builds the already-on-file message from the body's own fields.
 *
 * `form_type` and `received_at` arrive separately so this does not have to read
 *   them out of `message`, whose wording is aimed at the log rather than at a
 *   therapist. Falls back to whatever is available if either field is missing.
 *
 * **It says "on file", not "signed", and the difference is not cosmetic.** The
 *   backend's `409` criterion is an *archived document* for the case and form
 *   type — a `case_files3` row with the upload field populated — not a
 *   signature. Two ways those come apart:
 *
 *   - an intake uploaded by hand into Zoho carries the upload time and no
 *     signature at all, so "already signed on <date>" asserts something that
 *     never happened
 *   - a delayed or redelivered provider callback stamps the callback's moment
 *     rather than the patient's
 *
 * So the sentence describes what the timestamp actually measures: the document
 *   being on file since then. That is also the weaker claim, which is the right
 *   one to make to someone who may be about to tell a patient they already
 *   signed.
 */
const getAlreadySignedMessage = (data?: {
  form_type?: string;
  received_at?: string;
}): string => {
  const form = data?.form_type
    ? FORM_TYPE_LABELS[data.form_type] || 'form'
    : 'form';
  // `received_at` is ISO8601 with an explicit offset, so the instant is
  //   unambiguous. Which *date* to show is a separate question, and it is not
  //   settled by the offset: `2026-08-02T02:00:00+00:00` is Aug 2 in the string
  //   and Aug 1 for a therapist in Los Angeles. The date shown is the one they
  //   lived through, which is the only one they can match against their own
  //   memory of the visit — so it goes through the same conversion as every
  //   other timestamp in the app.
  const receivedAt = toUserTimezone(data?.received_at);

  return receivedAt.isValid()
    ? `This ${form} is already on file, from ${receivedAt.format('MMM D, YYYY')}.`
    : `This ${form} is already on file.`;
};

/**
 * Chooses what to tell the user when `send_form` fails.
 *
 * The backend distinguishes its failures by status, and two of them carry
 *   information the generic message would throw away.
 */
export const getRequestFormErrorMessage = (
  response?: ErrorResponse,
): string => {
  // `data` is a string on 400, which carries nothing worth surfacing.
  const data = typeof response?.data === 'object' ? response.data : undefined;
  const message = data?.message;

  switch (response?.status) {
    // Not a failure the therapist caused, and not one to retry: the document
    //   exists and is signed. Naming the form and the date is what lets them
    //   tell "already done" apart from "something broke".
    case FORM_ALREADY_SIGNED_STATUS:
      return getAlreadySignedMessage(data);

    // Names the template field that was rejected, which is the one thing ops
    //   can act on — so it is shown verbatim.
    case TEMPLATE_FIELD_MISMATCH_STATUS:
      return message || REQUEST_FORM_GENERIC_ERROR;

    // The provider refused our account, not the therapist. Its wording
    //   ("BoldSign /v1/template/send returned 403") means nothing to them, so
    //   that stays in the log and they get something honest and non-actionable.
    case SIGNING_PROVIDER_UNAVAILABLE_STATUS:
      return SIGNING_UNAVAILABLE_MESSAGE;

    default:
      return REQUEST_FORM_GENERIC_ERROR;
  }
};
